export const MG_CONSUMER_LOGIN_ROUTE = "/payment_portal/login";

/** Set when the user signs in from the payment portal login route; cleared on sign-out. */
export const MG_PAYMENT_PORTAL_SESSION_KEY = "mg_payment_portal_sign_in";

export const DEFAULT_COOKIE_OPTS = { path: "/", secure: true };

/** True when the current browser session signed in via the payment portal route. */
export const isPaymentPortalSession = () => {
  try {
    return (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(MG_PAYMENT_PORTAL_SESSION_KEY) === "1"
    );
  } catch {
    return false;
  }
};

/** True when auth user or session indicates a payment-portal (consumer) login. */
export const isPaymentPortalUser = (authUser) =>
  authUser?.from_payment_portal === true || isPaymentPortalSession();

/**
 * Shared cookie-based login check used by AuthContext, AppRouter, and api.js.
 * Payment-portal sessions may omit a refresh token.
 */
export const isAuthenticatedFromCookies = (
  cookies,
  cookieOpts = DEFAULT_COOKIE_OPTS
) => {
  const authUser = cookies.get("authUser", cookieOpts) || {};
  const hasLoginCookie = Boolean(cookies.get("isLoggedIn", cookieOpts));
  const hasAccessToken = Boolean(cookies.get("accessToken", cookieOpts));
  const hasRefreshToken = Boolean(cookies.get("refreshToken", cookieOpts));
  const isPaymentPortal = isPaymentPortalUser(authUser);
  return (
    hasLoginCookie && hasAccessToken && (hasRefreshToken || isPaymentPortal)
  );
};

/**
 * Where to send the user after sign-out: payment portal login vs app login.
 */
export const getPostSignOutLoginPath = (authUser) =>
  isPaymentPortalUser(authUser) ? MG_CONSUMER_LOGIN_ROUTE : "/login";

export const ALLOWED_PATHS = [
  "/login",
  MG_CONSUMER_LOGIN_ROUTE,
  "/team_registration",
  "/reset-password",
  "/disconnect",
  "/EmailVerifyError",
  "/payment",
  "/register",
  "/sso-success",
  "/qb-success",
  "/welcome-screen/login",
  "/welcome-screen/accounts",
  "/welcome-screen/accounts/000000",
  "/welcome-screen/payment",
  "/sso_dashboard",
  "/users/:id/password/edit",
  "/users/:id/set_password",
  "/choose-password",
];

export const COOKIE_KEYS = [
  "authUser",
  "isLoggedIn",
  "accessToken",
  "refreshToken",
];

export const DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

export const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `Day ${i + 1}`,
}));

export const DAY_OF_QUARTER_OPTIONS = Array.from({ length: 92 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `Day ${i + 1}`,
}));

export const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

export const MONTH_OF_QUARTER_OPTIONS = [
  { value: "first_month", label: "1st Month of Quarter" },
  { value: "second_month", label: "2nd Month of Quarter" },
  { value: "third_month", label: "3rd Month of Quarter" },
];

export const QBO_ONLY_REPORTS = ["QBO ACCOUNT SYNC COMPATIBILITY REPORT"];

export const COMMUNICATION_REPORT_SLUG = "communication_report";

/** The Communication Report's date range always filters on the sent date. */
export const COMMUNICATION_REPORT_DATE_TYPE = "sent_date";

/** Communication Report "Type" filter; the empty "All" value sends no filter_by[]. */
export const COMMUNICATION_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "Email", label: "Email" },
  { value: "Letter", label: "Letter" },
  { value: "SMS", label: "Text" },
];

/** Human-readable label for the Auto Populate Balance Due miscellaneous setting. */
export const AUTO_POPULATE_BALANCE_DUE_TITLE = "Auto Populate Balance Due";

/** Returns is_enable for Auto Populate Balance Due, or undefined if the setting is absent. */
export const getAutoPopulateBalanceDueEnabled = (miscellaneousGroups) => {
  const autoPopulate = miscellaneousGroups?.find(
    (item) => item.title === AUTO_POPULATE_BALANCE_DUE_TITLE
  );
  if (autoPopulate?.data?.length > 0) {
    return autoPopulate.data[0].is_enable;
  }
  return undefined;
};
